import { AxiosError } from 'axios';

/**
 * Standard API error response structure
 */
export interface ApiErrorResponse {
  success: false;
  error: string;
  errors?: Record<string, string[]>;
}

/**
 * Error codes with user-friendly messages
 */
const ERROR_MESSAGES: Record<number, string> = {
  400: 'Invalid request. Please check your input.',
  401: 'Session expired. Please login again.',
  403: 'You do not have access to this resource. Please contact your administrator.',
  404: 'The requested resource was not found.',
  409: 'This record already exists.',
  422: 'Please check the form for errors.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'Something went wrong. Please try again later.',
  502: 'Server is temporarily unavailable. Please try again later.',
  503: 'Service is temporarily unavailable. Please try again later.',
};

/**
 * Extract a user-friendly error message from any error
 */
export function getErrorMessage(error: unknown): string {
  // Handle Axios errors
  if (isAxiosError(error)) {
    const status = error.response?.status;
    const serverMessage = error.response?.data?.error;

    // Use server message if available
    if (serverMessage) {
      return serverMessage;
    }

    // Use status-based message
    if (status && ERROR_MESSAGES[status]) {
      return ERROR_MESSAGES[status];
    }

    // Network error
    if (error.code === 'ERR_NETWORK') {
      return 'Unable to connect to server. Please check your internet connection.';
    }

    // Timeout
    if (error.code === 'ECONNABORTED') {
      return 'Request timed out. Please try again.';
    }

    return error.message || 'An unexpected error occurred.';
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  return 'An unexpected error occurred.';
}

/**
 * Extract field-level validation errors from API response
 * Returns null if no field errors are present
 */
export function getFieldErrors(error: unknown): Record<string, string> | null {
  if (!isAxiosError(error)) {
    return null;
  }

  const errors = error.response?.data?.errors;
  if (!errors || typeof errors !== 'object') {
    return null;
  }

  const fieldErrors: Record<string, string> = {};
  for (const [field, messages] of Object.entries(errors)) {
    if (Array.isArray(messages) && messages.length > 0) {
      fieldErrors[field] = messages[0]; // First error message per field
    }
  }

  return Object.keys(fieldErrors).length > 0 ? fieldErrors : null;
}

/**
 * Check if error is an Axios error
 */
export function isAxiosError(error: unknown): error is AxiosError<ApiErrorResponse> {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  );
}

/**
 * Check if error is an authentication error (401)
 */
export function isAuthError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 401;
}

/**
 * Check if error is a permission error (403)
 */
export function isPermissionError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 403;
}

/**
 * Check if error is a validation error (422)
 */
export function isValidationError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 422;
}

/**
 * Check if error is a not found error (404)
 */
export function isNotFoundError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 404;
}

/**
 * Check if error is a server error (5xx)
 */
export function isServerError(error: unknown): boolean {
  if (!isAxiosError(error) || !error.response?.status) {
    return false;
  }
  return error.response.status >= 500 && error.response.status < 600;
}

/**
 * Get HTTP status code from error
 */
export function getErrorStatus(error: unknown): number | null {
  if (isAxiosError(error)) {
    return error.response?.status ?? null;
  }
  return null;
}
